import * as React from "react";
import { LibraryData } from "../interfaces";
import Search from "./Search";
import Button, { NavButton, AnchorButton } from "./Button";
import Link from "./Link";
import BookIcon from "../icons/Book";
import useLibraryContext from "./context/LibraryContext";
import { Text } from "./Text";
import Stack from "./Stack";
import { AccountMenu } from "./AccountMenu";
import useUser from "components/context/UserContext";
import useLogin from "auth/useLogin";
import useSignOutFlow from "hooks/useSignOutFlow";
import ClientOnly from "./ClientOnly";
import LanguageSelector from "./LanguageSelector";
import PinButton from "./PinButton";
import { useTranslation } from "next-i18next/pages";

const HeaderFC: React.FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation();
  const library = useLibraryContext();

  return (
    <header
      sx={{
        display: "flex",
        flexDirection: ["column", "column", "row"],
        alignItems: "stretch",
        px: [3, 5],
        py: 3
      }}
      className={className}
    >
      <Link
        href="/"
        aria-label={t(
          "header.catalogLink.ariaLabel",
          "Go to catalog home page"
        )}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: ["center", "flex-start"],
          textAlign: "center",
          p: 3,
          mb: [1, 0]
        }}
      >
        {library.logoUrl ? (
          <img
            src={library.logoUrl}
            alt={t("header.catalogLogo", "{{catalogName}} Logo", {
              catalogName: library.catalogName
            })}
          />
        ) : (
          <Text variant="text.headers.primary">{library.catalogName}</Text>
        )}
      </Link>
      <Stack
        direction="column"
        spacing={4}
        sx={{
          alignItems: ["stretch", "flex-end"],
          flex: 1
        }}
      >
        <HeaderLinks library={library} />
        <Search sx={{ minWidth: ["initial", 370], mr: [3, 0] }} />
      </Stack>
    </header>
  );
};

const HeaderLinks: React.FC<{ library: LibraryData }> = ({ library }) => {
  const { t } = useTranslation();
  const { helpWebsite } = library.libraryLinks;
  const { isAuthenticated, isLoading } = useUser();
  const signOutFlow = useSignOutFlow();
  const { baseLoginUrl } = useLogin();

  return (
    <div
      sx={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: ["center", "flex-end"]
      }}
    >
      <NavButton
        variant="ghost"
        color="ui.black"
        href="/"
        sx={{ whiteSpace: "initial" }}
      >
        {t("nav.catalog", "Catalog", { ns: "common" })}
      </NavButton>

      {library?.headerLinks?.map(link => (
        <AnchorButton
          variant="ghost"
          color="ui.black"
          href={link.href}
          title={link.title}
          key={link.href}
        >
          {link.title}
        </AnchorButton>
      ))}

      {helpWebsite && (
        <AnchorButton
          variant="ghost"
          color="ui.black"
          href={helpWebsite.href}
          title={t("header.help", "Help")}
        >
          {t("header.help", "Help")}
        </AnchorButton>
      )}

      <NavButton
        variant="ghost"
        color="ui.black"
        href="/loans"
        iconLeft={BookIcon}
      >
        {t("nav.myBooks", "My Books", { ns: "common" })}
      </NavButton>

      <ClientOnly>
        {isAuthenticated ? (
          <AccountMenu />
        ) : isLoading ? (
          <Button loading />
        ) : (
          <NavButton color="ui.black" href={baseLoginUrl}>
            {t("header.signIn", "Sign In")}
          </NavButton>
        )}
      </ClientOnly>

      <ClientOnly>
        <PinButton
          library={{
            id: library.id,
            slug: library.slug,
            title: library.catalogName,
            logoUrl: library.logoUrl ?? undefined
          }}
          signOut={signOutFlow}
          sx={{ ml: 3 }}
        />
      </ClientOnly>

      <LanguageSelector sx={{ ml: 3 }} />
    </div>
  );
};

export default HeaderFC;
